import { PlusIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Alert, Flex } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';

import { listAdminBooks } from '@/entities/books/api/admin-books-api';
import {
  Book,
  BookStatusTabEnum,
  GetBooks,
} from '@/entities/books/model/types';
import { BookSearch } from '@/features/book-search';
import { BookTabs } from '@/features/book-tabs/ui/book-tabs';
import { ServerPaginationControl } from '@/features/pagination-control/ui/server-pagination-control';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';
import { Button } from '@/shared/ui';

import styles from './books-page.module.scss';

export const BooksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const withProject = useProjectPath();
  const project = useCurrentProject();

  // Get initial values from URL params or use defaults
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialPerPage = parseInt(searchParams.get('perPage') || '10', 10);
  const initialTab =
    (searchParams.get('tab') as BookStatusTabEnum) || BookStatusTabEnum.All;
  const initialSearch = searchParams.get('search') || '';

  const [booksData, setBooksData] = useState<GetBooks>({
    books: [],
    count: 0,
    totalPages: 0,
    currentPage: initialPage,
    limit: initialPerPage,
    offset: 0,
  });
  const [activeTab, setActiveTab] = useState<BookStatusTabEnum>(initialTab);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  const fetchBooks = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const offset = (currentPage - 1) * perPage;

        // Convert tab status to published parameter
        let published: boolean | undefined;
        if (activeTab === BookStatusTabEnum.Published) {
          published = true;
        } else if (activeTab === BookStatusTabEnum.Draft) {
          published = false;
        }
        // For "All" tab, we don't send the published parameter

        const response = await listAdminBooks(
          project,
          {
            limit: perPage,
            offset,
            ...(published !== undefined && { published }),
            ...(searchQuery.trim() && { searchQuery: searchQuery.trim() }),
          },
          signal,
        );

        // Ensure we have valid response data
        const books = Array.isArray(response.books) ? response.books : [];
        const count = typeof response.count === 'number' ? response.count : 0;

        // Calculate total pages from count and limit
        const totalPages = Math.ceil(count / perPage);

        setBooksData({
          books,
          count,
          totalPages,
          currentPage,
          limit: perPage,
          offset,
        });
      } catch (error) {
        if (signal?.aborted) return;
        console.error('Error fetching books:', error);
        setError('Ошибка при загрузке книг. Попробуйте обновить страницу.');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [currentPage, perPage, activeTab, searchQuery, project],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchBooks(controller.signal);
    return () => controller.abort();
  }, [fetchBooks]);

  // Books are already filtered on the server side
  const filteredBooks = booksData.books || [];

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (value !== null) {
        setActiveTab(value as BookStatusTabEnum);
        setCurrentPage(1); // Reset to first page when changing tabs

        // Update URL params
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('tab', value);
          newParams.set('page', '1');
          return newParams;
        });
      }
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);

      // Update URL params
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('page', page.toString());
        return newParams;
      });
    },
    [setSearchParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => {
      setPerPage(newPerPage);
      setCurrentPage(1); // Reset to first page when changing per page

      // Update URL params
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('perPage', newPerPage.toString());
        newParams.set('page', '1');
        return newParams;
      });
    },
    [setSearchParams],
  );

  const handleBookListUpdate = useCallback((updatedBooks: Book[]) => {
    setBooksData((prev) => ({
      ...prev,
      books: updatedBooks,
      count: prev.count - ((prev.books || []).length - updatedBooks.length), // Update count if books were deleted
    }));
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setCurrentPage(1); // Reset to first page when searching

      // Update URL params
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (value.trim()) {
          newParams.set('search', value);
        } else {
          newParams.delete('search');
        }
        newParams.set('page', '1');
        return newParams;
      });
    },
    [setSearchParams],
  );

  return (
    <div className={styles.container}>
      <h1>Книги</h1>
      <Flex justify="space-between" direction="column">
        <Flex justify="space-between" direction="row" align="center">
          <BookSearch value={searchQuery} onChange={handleSearchChange} />

          <NavLink to={withProject('/books/new')}>
            <Button className={styles.button}>
              <ActionIcon variant="transparent">
                <PlusIcon color="white" />
              </ActionIcon>
              Добавить книгу
            </Button>
          </NavLink>
        </Flex>

        {error && (
          <Alert color="red" mb="md">
            {error}
          </Alert>
        )}

        <BookTabs
          activeTab={activeTab}
          books={filteredBooks}
          onTabChange={handleTabChange}
          setBooksList={handleBookListUpdate}
          loading={loading}
        />

        {!loading && !error && (
          <ServerPaginationControl
            currentPage={currentPage}
            totalPages={booksData.totalPages || 1}
            perPage={perPage}
            totalCount={booksData.count}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        )}
      </Flex>
    </div>
  );
};
